import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useHasTrainedToday } from '../hooks/useHasTrainedToday';
import { usersDB } from '../lib/db/users';
import { trainingsDB } from '../lib/db/trainings';
import { schoolsDB } from '../lib/db/schools';
import { trackEvent, EVENTS } from '../lib/analytics';
import { formatDisplayTime } from '../lib/timeUtils';
import { leagueBadgePath } from '../lib/shurikens';
import { format, parseISO, startOfWeek, addDays } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { getDirection } from '../lib/i18n';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import SchoolLogo from '../components/common/SchoolLogo';
import CompletedDrillItem from '../components/training/CompletedDrillItem';
import FocusSelector from '../components/training/FocusSelector';
import Modal from '../components/common/Modal';

export default function Home() {
  const [userData, setUserData] = useState(null);
  const [todayTrainings, setTodayTrainings] = useState([]);
  const [weekSeconds, setWeekSeconds] = useState(0);
  const [recentTrainings, setRecentTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [editingDrills, setEditingDrills] = useState([]);
  const [editDrillState, setEditDrillState] = useState({ isOpen: false, drillIndex: null, minutes: 0, seconds: 0, selectedFocusIds: [], drillCategory: null });
  const [schoolData, setSchoolData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [modalState, setModalState] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'alert', variant: 'primary' });
  
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [user, today] = await Promise.all([
        usersDB.getUserData(currentUser.uid),
        trainingsDB.getTodayTrainings(currentUser.uid)
      ]);
      
      setUserData(user);
      setTodayTrainings(today);
      
      // Calculate this week's training
      const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
      
      const weekTrainings = await trainingsDB.getTrainings(currentUser.uid, {
        startDate: weekStart,
        endDate: new Date()
      });
      
      const totalSeconds = weekTrainings.reduce((sum, t) => sum + t.totalDurationSeconds, 0);
      setWeekSeconds(totalSeconds);
      
      // Get this week's trainings (grouped by day)
      setRecentTrainings(weekTrainings); // Show all week's trainings
      
      trackEvent(EVENTS.HOME_VIEWED);
    } catch (err) {
      console.error('Error fetching home data:', err);
      setError(err);
      trackEvent(EVENTS.ERROR_OCCURRED, {
        errorType: 'fetch_home_data',
        errorMessage: err.message
      });
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [currentUser]);

  // Fetch school data when userData is available
  useEffect(() => {
    const fetchSchoolData = async () => {
      if (!userData?.profile?.schoolId) return;
      
      try {
        const school = await schoolsDB.getSchool(userData.profile.schoolId);
        setSchoolData(school);
      } catch (err) {
        console.error('Error fetching school data:', err);
      }
    };
    
    fetchSchoolData();
  }, [userData]);

  // Initialize editing drills when training is selected
  useEffect(() => {
    if (selectedTraining) {
      setEditingDrills([...selectedTraining.drills]);
      setHasChanges(false);
    }
  }, [selectedTraining]);

  // Training modal handlers
  const handleOpenTrainingModal = (training) => {
    setSelectedTraining(training);
  };

  const handleCloseTrainingModal = () => {
    if (hasChanges) {
      showModal(
        t('progress.unsaved_changes') || 'Unsaved Changes',
        t('progress.unsaved_changes_message') || 'You have unsaved changes. Are you sure you want to close?',
        () => {
          setHasChanges(false);
          setSelectedTraining(null);
          setEditingDrills([]);
        },
        'confirm',
        'warning'
      );
    } else {
      setSelectedTraining(null);
      setEditingDrills([]);
    }
  };

  const showModal = (title, message, onConfirm = null, type = 'alert', variant = 'primary') => {
    setModalState({ isOpen: true, title, message, onConfirm, type, variant });
  };

  const hideModal = () => {
    setModalState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'alert', variant: 'primary' });
  };

  const handleModalConfirm = () => {
    if (modalState.onConfirm) {
      modalState.onConfirm();
    }
    hideModal();
  };

  // Edit drill handlers
  const handleEditDrill = async (drill, drillIndex) => {
    const minutes = Math.floor(drill.durationSeconds / 60);
    const seconds = drill.durationSeconds % 60;
    const selectedFocusIds = drill.focus || [];
    
    // Fetch drill to get category for focus selection
    let drillCategory = null;
    if (userData?.profile?.schoolId && drill.drillId && drill.drillId !== 'custom') {
      try {
        const drillData = await schoolsDB.getDrill(userData.profile.schoolId, drill.drillId);
        drillCategory = drillData?.category || null;
      } catch (err) {
        console.error('Error fetching drill for category:', err);
      }
    }
    
    setEditDrillState({ isOpen: true, drillIndex, minutes, seconds, selectedFocusIds, drillCategory });
  };

  const handleCloseEditDrill = () => {
    setEditDrillState({ isOpen: false, drillIndex: null, minutes: 0, seconds: 0, selectedFocusIds: [], drillCategory: null });
  };

  // Close modals on Escape key and prevent body scroll
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (editDrillState.isOpen) {
          handleCloseEditDrill();
        } else if (selectedTraining) {
          handleCloseTrainingModal();
        }
      }
    };
    
    if (selectedTraining || editDrillState.isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEscape);
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTraining, editDrillState.isOpen]);
  
  // Use hook to check if user has trained today - MUST be called before any conditional returns
  const isTodayStreakCompleted = useHasTrainedToday(todayTrainings || []);
  
  if (loading) {
    return <LoadingSpinner message={t('common.loading')} />;
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 p-4">
        <div className="max-w-4xl mx-auto">
          <ErrorMessage 
            error={error} 
            onRetry={fetchData}
            className="mt-8"
          />
        </div>
      </div>
    );
  }
  
  if (!userData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 p-4">
        <div className="max-w-4xl mx-auto">
          <ErrorMessage 
            error={{ message: t('error.not_found') }} 
            onRetry={fetchData}
            className="mt-8"
          />
        </div>
      </div>
    );
  }
  
  const todaySeconds = todayTrainings.reduce((sum, t) => sum + t.totalDurationSeconds, 0);
  const todayMinutes = Math.floor(todaySeconds / 60);
  const todayDrills = todayTrainings.reduce((sum, t) => sum + t.drills.length, 0);
  
  // Calculate daily goal progress percentage
  const dailyGoalProgress = Math.min((todayMinutes / userData.settings.dailyGoalMinutes) * 100, 100);
  
  // Get streak milestone info
  const getStreakMilestone = (streak) => {
    if (streak >= 30) return { emoji: '🏆', message: t('home.milestone_30') };
    if (streak >= 14) return { emoji: '🥇', message: t('home.milestone_14') };
    if (streak >= 7) return { emoji: '🥈', message: t('home.milestone_7') };
    return { emoji: '🔥', message: t('home.keep_going') };
  };
  
  const streakMilestone = getStreakMilestone(userData.stats.currentStreak);
  
  // Get current time in school timezone for time calculations
  const schoolTimezone = 'Asia/Jerusalem';
  const now = toZonedTime(new Date(), schoolTimezone);
  
  // Check if user has an active streak (currentStreak > 0)
  const hasStreak = userData.stats.currentStreak > 0;
  
  // Calculate time remaining until midnight (in school timezone)
  const getTimeRemainingToday = () => {
    const midnight = new Date(now);
    midnight.setHours(23, 59, 59, 999);
    const diffMs = midnight.getTime() - now.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };
  
  // Get inspirational message based on state
  const getInspirationalMessage = () => {
    if (isTodayStreakCompleted) {
      // Trained already today
      const daysWord = userData.stats.currentStreak === 1 ? t('home.day_singular') : t('home.day_plural');
      return t('home.streak_message_trained', {
        count: userData.stats.currentStreak,
        days: daysWord
      });
    } else if (hasStreak) {
      // Has streak but not trained today
      const timeLeft = getTimeRemainingToday();
      return t('home.streak_message_not_trained', { timeLeft });
    } else {
      // No streak (didn't train yesterday or streak is 0)
      return t('home.streak_message_no_streak');
    }
  };
  
  const inspirationalMessage = getInspirationalMessage();
  
  // Get card background color - gray if not trained today, orange if trained
  const cardBgClass = isTodayStreakCompleted 
    ? 'bg-gradient-to-r from-accent-500 via-orange-500 to-red-500'
    : 'bg-gradient-to-r from-gray-400 via-gray-500 to-gray-600';
  
  // Get localized day abbreviation
  const getDayAbbr = (date) => {
    const dayNum = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return t(`common.days_abbr.${dayNames[dayNum]}`);
  };

  const handleSaveDrillEdit = () => {
    const { drillIndex, minutes, seconds, selectedFocusIds } = editDrillState;
    if (drillIndex === null) return;
    
    const newDurationSeconds = minutes * 60 + seconds;
    if (newDurationSeconds <= 0) {
      showModal(
        t('training.edit_drill_invalid_time'),
        t('training.edit_drill_invalid_time_message'),
        null,
        'alert',
        'warning'
      );
      return;
    }
    
    setEditingDrills(prev => {
      const updated = prev.map((drill, idx) => {
        if (idx === drillIndex) {
          // First edit: set originDurationSeconds to current durationSeconds
          // Subsequent edits: preserve originDurationSeconds
          const originDurationSeconds = drill.originDurationSeconds !== undefined 
            ? drill.originDurationSeconds 
            : drill.durationSeconds;
          
          return {
            ...drill,
            durationSeconds: newDurationSeconds,
            originDurationSeconds,
            focus: selectedFocusIds.length > 0 ? [...selectedFocusIds] : []
          };
        }
        return drill;
      });
      setHasChanges(true);
      return updated;
    });
    
    handleCloseEditDrill();
  };

  // Get applicable focus items for a drill by category
  const getApplicableFocusForCategory = (category) => {
    if (!schoolData?.focus || !category) return [];
    
    return schoolData.focus.filter(focusItem => 
      focusItem.categories && focusItem.categories.includes(category)
    );
  };

  const handleDeleteDrill = () => {
    const { drillIndex } = editDrillState;
    if (drillIndex === null) return;
    
    showModal(
      t('training.delete_drill_confirm'),
      t('training.delete_drill_confirm_message'),
      () => {
        setEditingDrills(prev => {
          const updated = prev.filter((_, idx) => idx !== drillIndex);
          setHasChanges(true);
          return updated;
        });
        handleCloseEditDrill();
      },
      'confirm',
      'danger'
    );
  };

  const handleSaveTraining = async () => {
    if (editingDrills.length === 0) {
      showModal(
        t('training.no_drills_error'),
        t('training.no_drills_error_message') || '',
        null,
        'alert',
        'warning'
      );
      return;
    }

    setSaving(true);
    
    try {
      await trainingsDB.updateTraining(currentUser.uid, selectedTraining.id, {
        drills: editingDrills,
        sessionNotes: selectedTraining.sessionNotes || null
      });
      
      trackEvent(EVENTS.TRAINING_SESSION_EDITED, {
        sessionId: selectedTraining.id,
        drillCount: editingDrills.length
      });
      
      setHasChanges(false);
      // Refresh data
      await fetchData();
      handleCloseTrainingModal();
    } catch (err) {
      console.error('Error updating training:', err);
      showModal(
        t('error.server_error'),
        err.message,
        null,
        'alert',
        'danger'
      );
    } finally {
      setSaving(false);
    }
  };

  const formatSessionTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-lg shadow-md border-b border-gray-100">
        <div className="px-4 md:max-w-4xl md:mx-auto md:px-6 py-4 md:py-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/settings')}
              className="h-[60px] w-[60px] rounded-2xl bg-white/80 border border-white/70 shadow-sm flex items-center justify-center hover:bg-white hover:shadow-md transition-all cursor-pointer active:scale-95"
              title={t('navigation.settings')}
            >
              <SchoolLogo 
                size="large" 
                showEmoji={false} 
                className="!h-full !w-auto max-h-full object-contain"
              />
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="font-bold text-xl md:text-3xl text-primary-600 hover:text-primary-700 transition-colors cursor-pointer active:scale-95"
              title={t('navigation.settings')}
            >
              {userData.profile.name}
            </button>
          </div>
          <div className="flex gap-2 md:gap-3">
            <button 
              onClick={() => navigate('/settings')}
              className="p-2 md:p-3 text-xl md:text-2xl hover:bg-primary-50 rounded-xl transition-all hover:scale-110 active:scale-95 min-w-[44px] min-h-[44px] flex items-center justify-center"
              title={t('navigation.settings')}
            >
              ⚙️
            </button>
            <button
              onClick={() => navigate('/progress')}
              className="p-2 md:p-3 text-xl md:text-2xl hover:bg-primary-50 rounded-xl transition-all hover:scale-110 active:scale-95 min-w-[44px] min-h-[44px] flex items-center justify-center"
              title={t('navigation.progress')}
            >
              📊
            </button>
            {/* Leaderboard - hidden until feature is complete
            <button
              onClick={() => navigate('/leaderboard')}
              className="p-2 md:p-3 text-xl md:text-2xl hover:bg-primary-50 rounded-xl transition-all hover:scale-110 active:scale-95 min-w-[44px] min-h-[44px] flex items-center justify-center"
              title={t('leaderboard.title')}
            >
              🏆
            </button>
            */}
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="px-4 py-4 md:max-w-4xl md:mx-auto md:px-6 md:py-8 flex flex-col min-h-[calc(100vh-80px)] md:min-h-auto">
        <div className="space-y-3 md:space-y-6 flex-1">
          {/* Streak Card - Reduced height by 30% */}
          <div onClick={() => navigate('/training')}
               className={`${cardBgClass} happy-sad-ninja rounded-2xl p-4 md:p-8 text-white shadow-2xl animate-slide-up relative overflow-hidden`}>
            {/* Ninja Image - Left side for LTR, Right side for RTL */}
            <div className={`absolute top-0 bottom-0 flex items-center ${getDirection(i18n.language) === 'rtl' ? 'right-4 md:right-8' : 'left-4 md:left-8'}`}>
              <img
                src={isTodayStreakCompleted ? '/ninja-happy.png' : '/ninja-sad.png'}
                alt={isTodayStreakCompleted ? 'Happy ninja' : 'Sad ninja'}
                className="h-full w-auto object-contain transition-opacity duration-300 max-h-full"
              />
            </div>
            <div className="flex items-center">
              <div className="ms-[90px]">
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-sm md:text-xl font-semibold opacity-90">{t('home.streak', {count: userData.stats.currentStreak})}</div>
                  <div className={`text-lg md:text-2xl transition-opacity ${isTodayStreakCompleted ? 'opacity-100' : 'opacity-30'}`}>
                    💡
                  </div>
                </div>
                <div className="text-2xl md:text-5xl font-bold mb-1">
                  <span className={`inline-block transition-opacity ${isTodayStreakCompleted ? 'opacity-100' : 'opacity-30 grayscale'}`}>
                    {streakMilestone.emoji}
                  </span>{' '}
                  {userData.stats.currentStreak}
                </div>
                <div className="text-xs md:text-lg opacity-90 font-medium">{inspirationalMessage}</div>
              </div>
            </div>
          </div>
          
          {/* Stats Grid - Compact design, max-height: 120px, 12px padding */}
          <div className="grid grid-cols-3 gap-2 md:gap-6">
            {/* Daily Goal */}
            <div className="bg-white rounded-xl p-3 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 card-hover max-h-[120px] flex flex-col justify-between">
              <div className="text-gray-600 text-xs md:text-base font-semibold">{t('home.daily_goal')}</div>
              <div className="text-xl md:text-3xl font-bold">
                {todayMinutes}/{userData.settings.dailyGoalMinutes}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-primary-600 to-primary-400 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${dailyGoalProgress}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-600 font-medium">
                {dailyGoalProgress.toFixed(0)}%
              </div>
            </div>
            
            {/* Weekly Time */}
            <div className="bg-white rounded-xl p-3 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 card-hover max-h-[120px] flex flex-col justify-between">
              <div className="text-gray-600 text-xs md:text-base font-semibold">{t('home.weekly_time')}</div>
              <div className="text-xl md:text-3xl font-bold text-primary-600">
                {formatDisplayTime(weekSeconds, t)}
              </div>
            </div>
            
            {/* Today's Drills */}
            <div 
              onClick={() => navigate('/training')}
              className="bg-white rounded-xl p-3 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 card-hover max-h-[120px] flex flex-col justify-between cursor-pointer active:scale-95"
            >
              <div className="text-gray-600 text-xs md:text-base font-semibold">{t('home.drills_today')}</div>
              {isTodayStreakCompleted ? (
                <>
                  <div className="text-xl md:text-3xl font-bold text-success-600">✅ {todayMinutes}</div>
                  <div className="text-xs text-gray-600 font-medium">
                    {t('training.minutes')}
                  </div>
                </>
              ) : (
                <div className="text-xl md:text-3xl font-bold text-red-600">❌</div>
              )}
            </div>
          </div>
          
          {/* Shuriken Row - weekly + total (like Duolingo XP) */}
          {(() => {
            const shurikens = userData.stats.shurikens || { total: 0, weekly: 0 };
            const league = userData.stats.currentLeague || 'lead';
            const multiplier = userData.stats.shurikens?.multiplier || 1.0;
            const isMultiplierActive = multiplier > 1.0;
            return (
              <div className="grid grid-cols-2 gap-2 md:gap-6">
                {/* Weekly Shurikens */}
                <div className="bg-white rounded-xl p-3 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 card-hover flex flex-col justify-between min-h-[100px]">
                  <div className="flex items-center gap-1 text-gray-600 text-xs md:text-base font-semibold">
                    <img src="/shurikan-silver.png" alt="" className="w-7 h-7 md:w-8 md:h-8 object-contain" />
                    {t('home.weekly_shurikens', 'This week')}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xl md:text-3xl font-bold">{shurikens.weekly || 0}</span>
                    {isMultiplierActive && (
                      <span className="text-xs bg-yellow-100 text-yellow-700 rounded-full px-2 py-0.5 font-semibold">
                        ×{multiplier.toFixed(1)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <img
                      src={leagueBadgePath(league)}
                      alt={league}
                      className="w-5 h-5 md:w-6 md:h-6 object-contain"
                    />
                    <span className="text-xs text-gray-500 capitalize font-medium">{league}</span>
                  </div>
                </div>

                {/* Total Shurikens */}
                <div className="bg-white rounded-xl p-3 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 card-hover flex flex-col justify-between min-h-[100px]">
                  <div className="flex items-center gap-1 text-gray-600 text-xs md:text-base font-semibold">
                    <img src="/shurikan-gold.png" alt="" className="w-7 h-7 md:w-8 md:h-8 object-contain" />
                    {t('home.total_shurikens', 'All time')}
                  </div>
                  <div className="text-xl md:text-3xl font-bold mt-1">{shurikens.total || 0}</div>
                </div>
              </div>
            );
          })()}

          {/* Recent Activity - Compact design, 48px items, 8px spacing */}
          {recentTrainings.length > 0 && (
            <div className="bg-white rounded-2xl p-3 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
              <h3 className="font-bold text-base md:text-2xl mb-3 text-gray-800">{t('home.recent_activity')}</h3>
              <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
                {recentTrainings.slice(0, 3).map((training, idx) => {
                  const date = parseISO(training.startedAt);
                  const dayAbbr = getDayAbbr(date); // Localized day abbreviation
                  const time = format(date, 'HH:mm');
                  const drillsCount = training.drills.length;
                  const minutes = Math.floor(training.totalDurationSeconds / 60);
                  
                  return (
                    <div 
                      key={idx} 
                      onClick={() => handleOpenTrainingModal(training)}
                      className="flex justify-between items-center py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 min-h-[48px] cursor-pointer active:scale-95"
                    >
                      <div className="font-semibold text-sm md:text-lg">
                        {dayAbbr} {time}
                      </div>
                      <div className="text-xs md:text-sm text-gray-600 font-medium">
                        {drillsCount} {t('home.drills')} • {minutes} {t('training.minutes')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        
        {/* Start Training Button - Fixed at bottom for thumb accessibility */}
        <div className="mt-auto pt-4 md:pt-6 pb-4 md:pb-8">
          <button
            onClick={() => navigate('/training')}
            className="w-full bg-primary-600 text-white py-4 md:py-12 rounded-2xl font-bold text-lg md:text-4xl shadow-2xl min-h-[52px] md:min-h-[110px] flex items-center justify-center gap-2 md:gap-3 transition-all duration-300 hover:scale-105 hover:shadow-glow active:scale-95"
          >
            <span>{t('home.start_training')}</span>
          </button>
        </div>
      </div>

      {/* Training Details Modal */}
      {selectedTraining && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
          onClick={handleCloseTrainingModal}
        >
          {/* Backdrop with blur */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          
          {/* Modal content */}
          <div 
            className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col transform animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-4 md:px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg md:text-2xl font-bold text-gray-800">
                    {t('progress.session_details') || 'Session Details'}
                  </h3>
                  <div className="text-sm md:text-base text-gray-600 mt-1">
                    {format(parseISO(selectedTraining.startedAt), 'MMM dd, yyyy HH:mm')}
                  </div>
                </div>
                <button
                  onClick={handleCloseTrainingModal}
                  className="text-2xl hover:bg-gray-100 rounded-full p-2 transition-colors"
                  title={t('common.close')}
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* Body - Scrollable */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-6">
              {/* Session Timer */}
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-4 md:p-6 mb-4 md:mb-6">
                <div className="text-center">
                  <div className="text-gray-600 text-sm md:text-xl font-semibold mb-2">
                    {t('training.total_time')}
                  </div>
                  <div className="text-2xl md:text-4xl font-mono font-bold text-primary-600">
                    {formatSessionTime(editingDrills.reduce((sum, d) => sum + d.durationSeconds, 0))}
                  </div>
                  <div className="text-xs md:text-sm text-gray-600 mt-2">
                    {editingDrills.length} {t('progress.drills') || t('home.drills')}
                  </div>
                </div>
              </div>

              {/* Completed Drills */}
              {editingDrills.length > 0 ? (
                <div className="bg-white rounded-2xl p-4 md:p-6 mb-4 md:mb-6 border border-gray-200">
                  <h3 className="font-bold mb-3 md:mb-4 text-base md:text-xl text-gray-800">
                    {t('training.completed_drills')} ({editingDrills.length})
                  </h3>
                  <div className="space-y-2 md:space-y-3">
                    {editingDrills.map((drill, idx) => (
                      <CompletedDrillItem
                        key={idx}
                        drill={drill}
                        index={idx}
                        onEdit={handleEditDrill}
                        continueDrillEnabled={false}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-2xl p-4 md:p-6 mb-4 md:mb-6 text-center text-gray-500">
                  {t('progress.no_drills_in_session') || 'No drills in this session'}
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="px-4 md:px-6 py-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
              <button
                onClick={handleCloseTrainingModal}
                className="px-4 md:px-6 py-2 md:py-3 border-2 border-gray-300 rounded-xl font-semibold text-sm md:text-base hover:bg-gray-50 text-gray-700 transition-all hover:scale-105 active:scale-95 flex-1"
              >
                {hasChanges ? t('common.cancel') : t('common.close')}
              </button>
              {hasChanges && (
                <button
                  onClick={handleSaveTraining}
                  disabled={saving || editingDrills.length === 0}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-xl font-semibold text-sm md:text-base shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95 flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? t('common.saving') || 'Saving...' : t('common.save')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Drill Popup */}
      {editDrillState.isOpen && editDrillState.drillIndex !== null && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fade-in"
          onClick={handleCloseEditDrill}
        >
          {/* Backdrop with blur */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          
          {/* Popup content */}
          <div 
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full transform animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-4 md:px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg md:text-2xl font-bold text-gray-800">
                  {t('training.edit_drill')}
                </h3>
                <button
                  onClick={handleCloseEditDrill}
                  className="text-2xl hover:bg-gray-100 rounded-full p-2 transition-colors"
                  title={t('common.close')}
                >
                  ×
                </button>
              </div>
              {editingDrills[editDrillState.drillIndex] && (
                <p className="text-sm md:text-lg text-gray-600 mt-2">
                  {editingDrills[editDrillState.drillIndex].drillName}
                </p>
              )}
            </div>
            
            {/* Body */}
            <div className="px-4 md:px-6 py-4 md:py-6">
              <div className="mb-4 md:mb-6">
                <label className="block text-sm md:text-xl font-semibold text-gray-700 mb-2 md:mb-4">
                  {t('training.edit_drill_time')}
                </label>
                <div className="flex items-center gap-2 md:gap-4" style={{ direction: 'ltr' }}>
                  <div className="flex-1">
                    <label className="block text-xs md:text-sm text-gray-600 mb-1 md:mb-2 text-center">
                      {t('training.minutes')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="999"
                      value={editDrillState.minutes}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setEditDrillState(prev => ({ ...prev, minutes: Math.max(0, value) }));
                      }}
                      className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-300 rounded-lg text-center text-base md:text-xl font-semibold focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                  <div className="pt-6 md:pt-8 text-xl md:text-3xl font-bold text-gray-400">:</div>
                  <div className="flex-1">
                    <label className="block text-xs md:text-sm text-gray-600 mb-1 md:mb-2 text-center">
                      {t('common.seconds_short')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={editDrillState.seconds}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setEditDrillState(prev => ({ ...prev, seconds: Math.max(0, Math.min(59, value)) }));
                      }}
                      className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-300 rounded-lg text-center text-base md:text-xl font-semibold focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                </div>
              </div>
              
              {/* Focus Selection */}
              {(() => {
                const applicableFocus = editDrillState.drillCategory 
                  ? getApplicableFocusForCategory(editDrillState.drillCategory) 
                  : [];
                
                if (applicableFocus.length === 0) return null;
                
                return (
                  <div className="mb-4 md:mb-6">
                    <FocusSelector
                      applicableFocus={applicableFocus}
                      selectedFocusIds={editDrillState.selectedFocusIds}
                      onFocusChange={(newFocusIds) => {
                        setEditDrillState(prev => ({ ...prev, selectedFocusIds: newFocusIds }));
                      }}
                    />
                  </div>
                );
              })()}
            </div>
            
            {/* Actions */}
            <div className="px-4 md:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-2 md:gap-3">
              <button
                onClick={handleDeleteDrill}
                className="px-4 md:px-6 py-2 md:py-3 border-2 border-red-500 text-red-600 rounded-xl font-semibold text-sm md:text-base hover:bg-red-50 active:bg-red-100 transition-all hover:scale-105 active:scale-95"
              >
                {t('training.delete_drill')}
              </button>
              <div className="flex gap-2 md:gap-3 flex-1">
                <button
                  onClick={handleCloseEditDrill}
                  className="px-4 md:px-6 py-2 md:py-3 border-2 border-gray-300 rounded-xl font-semibold text-sm md:text-base hover:bg-gray-50 text-gray-700 transition-all hover:scale-105 active:scale-95 flex-1"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSaveDrillEdit}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-xl font-semibold text-sm md:text-base shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95 flex-1"
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for alerts */}
      <Modal
        isOpen={modalState.isOpen}
        title={modalState.title}
        message={modalState.message}
        onConfirm={handleModalConfirm}
        onCancel={hideModal}
        type={modalState.type}
        variant={modalState.variant}
      />
    </div>
  );
}